import { createContext, useContext, useEffect, useState, useMemo } from "react";
import axios from "axios";

const PortalAuthContext = createContext(null);
export const usePortalAuth = () => useContext(PortalAuthContext);

const baseURL = `${process.env.REACT_APP_BACKEND_URL}/api/portal`;

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export function PortalAuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = logged out

  const api = useMemo(() => {
    const instance = axios.create({ baseURL, withCredentials: true });
    instance.interceptors.response.use(
      (r) => r,
      async (error) => {
        const original = error.config;
        if (error.response?.status === 401 && !original._retried && !original.url.includes("/login") && !original.url.includes("/refresh")) {
          original._retried = true;
          try {
            await instance.post("/refresh");
            return instance(original);
          } catch {
            setUser(false);
          }
        }
        return Promise.reject(error);
      },
    );
    return instance;
  }, []);

  useEffect(() => {
    api.get("/me").then((r) => setUser(r.data)).catch(() => setUser(false));
  }, [api]);

  const login = async (email, password) => {
    const { data } = await api.post("/login", { email, password });
    setUser(data);
    return data;
  };

  const logout = async () => {
    try {
      await api.post("/logout");
    } finally {
      setUser(false);
    }
  };

  return (
    <PortalAuthContext.Provider value={{ user, login, logout, api }}>
      {children}
    </PortalAuthContext.Provider>
  );
}
